import { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Plus, PackageSearch } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCurrency } from '@/contexts/CurrencyContext';
import { HealthProductCard } from '@/components/health/HealthProductCard';
import { FloatingShapes } from '@/components/health/FloatingShapes';
import { HEALTH_CATEGORIES, HERO_IMAGE } from '@/lib/health';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoryPills = ({ options, selected, onSelect }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar" data-testid="category-pills">
    {options.map((opt) => {
      const active = (selected || 'all') === opt.value;
      return (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`relative shrink-0 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${active ? 'text-white' : 'text-slate-600 bg-white border border-health-line hover:border-health-emerald hover:text-health-emerald'}`}
          data-testid={`category-pill-${opt.value.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        >
          {active && <motion.span layoutId="category-pill-active" className="absolute inset-0 rounded-full bg-health-emerald" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />}
          <span className="relative">{opt.label}</span>
        </button>
      );
    })}
  </div>
);

const PageHeader = ({ category }) => {
  const meta = HEALTH_CATEGORIES.find((c) => c.name.toLowerCase() === (category || '').toLowerCase());
  return (
    <section className={`relative overflow-hidden pt-28 pb-14 ${meta ? meta.tint : 'bg-health-cream'}`}>
      <FloatingShapes className="hidden md:block top-16" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-12 gap-8 items-center">
        <motion.div key={category || 'all'} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="md:col-span-8">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-health-emerald">
            <Plus className="h-3 w-3" strokeWidth={3} /> {meta ? meta.badge : 'The whole shelf'}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-health-ink mt-3 mb-4" data-testid="products-page-title">
            {category || 'Shop all essentials'}
          </h1>
          <p className="text-slate-600 max-w-xl text-base md:text-lg">
            {meta ? meta.tagline : 'Over-the-counter relief, first aid and home health devices, picked to keep your family feeling good.'}
          </p>
        </motion.div>
        <motion.div key={`img-${category || 'all'}`} initial={{ opacity: 0, scale: 0.85, rotate: 6 }} animate={{ opacity: 1, scale: 1, rotate: 3 }} transition={{ type: 'spring', stiffness: 120 }} className="hidden md:block md:col-span-4">
          <img src={meta ? meta.image : HERO_IMAGE} alt="" className="w-full aspect-square object-cover rounded-[2rem] border-8 border-white shadow-lift" />
        </motion.div>
      </div>
    </section>
  );
};

const EmptyResults = ({ hasFilters, onClear }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-20 bg-white rounded-[2rem] border border-health-line" data-testid="products-empty-state">
    <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }} className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-health-mint flex items-center justify-center">
      <PackageSearch className="h-10 w-10 text-health-emerald" />
    </motion.div>
    <h3 className="font-heading text-2xl font-bold text-health-ink mb-2">{hasFilters ? 'Nothing matches just yet' : 'Shelves restocking soon'}</h3>
    <p className="text-slate-600 mb-6 max-w-md mx-auto">{hasFilters ? 'Try a different search or category.' : "We're adding fresh health essentials. Check back shortly!"}</p>
    {hasFilters && (
      <Button onClick={onClear} className="rounded-full h-12 px-8 bg-health-emerald hover:bg-health-emerald-dark text-white font-bold" data-testid="clear-filters-button">Clear all filters</Button>
    )}
  </motion.div>
);

const selectCls = 'w-full sm:w-44 h-11 rounded-full bg-white border-health-line text-slate-700 font-semibold';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category');
  const { formatLocalPrice } = useCurrency();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState('all');

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = selectedCategory ? `${API}/products?category=${encodeURIComponent(selectedCategory)}` : `${API}/products`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const categoryOptions = useMemo(() => {
    const names = [...new Set([...HEALTH_CATEGORIES.map((c) => c.name), ...categories])];
    return [{ value: 'all', label: 'All' }, ...names.map((n) => ({ value: n, label: n }))];
  }, [categories]);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) => p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q));
    }
    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(Number);
      result = result.filter((p) => (max ? p.price >= min && p.price <= max : p.price >= min));
    }
    if (sortBy === 'price-asc') result.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-desc') result.sort((a, b) => b.price - a.price);
    if (sortBy === 'name') result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [products, searchQuery, sortBy, priceRange]);

  const selectCategory = (value) => {
    if (value === 'all') searchParams.delete('category');
    else searchParams.set('category', value);
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSortBy('newest');
    setPriceRange('all');
    setSearchParams({});
  };

  const hasActiveFilters = Boolean(searchQuery || sortBy !== 'newest' || priceRange !== 'all' || selectedCategory);

  return (
    <div className="min-h-screen bg-background font-body text-health-ink">
      <Navbar />
      <PageHeader category={selectedCategory} />

      <section className="sticky top-20 z-40 bg-white/90 backdrop-blur-xl border-y border-health-line py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-4">
          <CategoryPills options={categoryOptions} selected={selectedCategory} onSelect={selectCategory} />
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search thermometers, plasters, cold relief..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-11 h-11 rounded-full bg-health-mint/50 border-health-line" data-testid="search-input" />
            </div>
            <div className="flex gap-3 flex-1 sm:justify-end">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className={selectCls} data-testid="sort-select"><SelectValue placeholder="Sort by" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priceRange} onValueChange={setPriceRange}>
                <SelectTrigger className={selectCls} data-testid="price-select"><SelectValue placeholder="Price" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All prices</SelectItem>
                  <SelectItem value="0-25">Under $25</SelectItem>
                  <SelectItem value="25-50">$25 - $50</SelectItem>
                  <SelectItem value="50-100">$50 - $100</SelectItem>
                  <SelectItem value="100-">Over $100</SelectItem>
                </SelectContent>
              </Select>
              <AnimatePresence>
                {hasActiveFilters && (
                  <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}>
                    <Button variant="ghost" onClick={clearFilters} className="h-11 rounded-full text-health-coral hover:bg-health-peach" data-testid="clear-filters-inline">
                      <X className="h-4 w-4 mr-1" /> Clear
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-slate-500 text-sm mb-8" data-testid="products-count">
            Showing <span className="text-health-ink font-bold">{filteredProducts.length}</span> products
          </p>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-3xl border border-health-line p-2 animate-pulse">
                  <div className="aspect-square bg-health-mint rounded-2xl" />
                  <div className="p-3 space-y-3">
                    <div className="h-3 bg-health-mint rounded-full w-1/3" />
                    <div className="h-4 bg-health-mint rounded-full w-full" />
                    <div className="h-5 bg-health-mint rounded-full w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredProducts.map((p, i) => <HealthProductCard key={p.id} product={p} formatLocalPrice={formatLocalPrice} index={i} />)}
            </div>
          ) : (
            <EmptyResults hasFilters={hasActiveFilters} onClear={clearFilters} />
          )}
          {!loading && filteredProducts.length === 0 && !hasActiveFilters && (
            <div className="mt-6 text-center">
              <Link to="/" className="text-sm font-semibold text-health-emerald hover:underline" data-testid="back-home-link">Back to home</Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
